/**
 * Central catalog of the 9 AI Assistants' use-case MODES, purely for discovery
 * surfaces (the ⌘K command palette). Each assistant page owns its own MODES
 * array for the form UI; this lightweight list mirrors the labels so users can
 * jump straight to a mode (e.g. "battle card" -> Sales › Battle Card).
 *
 * Deep-link: navigates to <route>?mode=<value>; each assistant page reads the
 * `mode` query param and preselects that mode.
 */
import { ASSISTANT_BY_KEY } from "@/lib/assistants";

// assistantKey -> { field, modes: [[value, label], ...] }
const MODES_BY_ASSISTANT = {
  proposals: {
    field: "report_type",
    modes: [
      ["full_proposal", "Full Proposal Draft"], ["capability_statement", "Capability Statement"],
      ["past_performance", "Past Performance / Case Study"], ["exec_summary_cover", "Exec Summary & Cover Letter"],
      ["pricing_boe", "Pricing & Basis of Estimate"], ["win_themes_strategy", "Win-Theme & Capture Strategy"],
      ["orals_presentation", "Orals / Pitch Narrative"], ["proposal_outline", "Annotated Proposal Outline"],
      ["rfp_shred", "RFP Shred & Compliance Matrix"], ["color_review", "Proposal Review (Color Team)"],
      ["compliance_check", "Compliance Gap Check"], ["bid_no_bid", "Bid / No-Bid Decision"],
    ],
  },
  grc: {
    field: "task_type",
    modes: [
      ["compliance_mapping", "Compliance Mapping"], ["gap_assessment", "Gap Assessment"],
      ["soa", "Statement of Applicability"], ["framework_crosswalk", "Framework Crosswalk"],
      ["risk_assessment", "Risk Assessment"], ["risk_treatment", "Risk Treatment Plan"],
      ["tprm", "Vendor Risk (TPRM)"], ["policy_review", "Policy Review"],
      ["policy_drafting", "Policy Drafting"], ["cert_readiness", "Certification Readiness"],
      ["audit_readiness", "Audit & Evidence Readiness"], ["bcdr_plan", "BCP / DR Plan + BIA"],
    ],
  },
  soc: {
    field: "report_type",
    modes: [
      ["incident_response", "Incident Response Playbook"], ["alert_triage", "Alert Triage & Investigation"],
      ["post_incident_review", "Post-Incident Review"], ["tabletop_exercise", "IR Tabletop Exercise"],
      ["threat_hunt", "Threat Hunt & Detection"], ["detection_engineering", "Detection Engineering"],
      ["use_case_development", "SIEM Use-Case Development"], ["log_source_review", "Logging & Visibility Review"],
      ["threat_intel_brief", "Threat-Intel Brief"], ["purple_team_plan", "Purple-Team Plan"],
      ["vuln_triage", "Vulnerability Triage"], ["soc_runbook", "SOC Runbook / SOP"],
    ],
  },
  cloud: {
    field: "report_type",
    modes: [
      ["posture_review", "Security Posture Review"], ["well_architected", "Well-Architected Review"],
      ["iam_review", "IAM / Least-Privilege Review"], ["container_k8s_security", "Container / K8s Security"],
      ["hardening_guide", "Hardening / IaC Guide"], ["iac_review", "IaC Security Review"],
      ["network_architecture", "Secure Network Design"], ["cost_optimization", "Cost (FinOps) Optimization"],
      ["devsecops_runbook", "DevSecOps Runbook"], ["migration_plan", "Secure Migration Plan"],
      ["backup_dr", "Backup & DR Design"], ["incident_runbook", "Cloud Incident Runbook"],
    ],
  },
  sales: {
    field: "report_type",
    modes: [
      ["research_targeting", "Account Research & Targeting"], ["value_prop_messaging", "Messaging / Positioning"],
      ["battle_card", "Competitive Battle Card"], ["discovery_questions", "Discovery Question Guide"],
      ["outreach_sequence", "Outreach Campaign"], ["proposal_email", "Proposal Follow-up Email"],
      ["objection_handling", "Objection-Handling Guide"], ["social_campaign", "Social Media Campaign"],
      ["content_collateral", "Marketing Content & Collateral"], ["case_study", "Case Study / Success Story"],
      ["newsletter", "Email Newsletter"], ["event_webinar", "Event / Webinar Plan"],
    ],
  },
  executive: {
    field: "report_type",
    modes: [
      ["board_brief", "Board Brief"], ["leadership_digest", "Leadership Digest"],
      ["executive_summary", "Executive Summary"], ["strategic_memo", "Strategy / Decision Memo"],
      ["risk_posture_report", "Risk Posture Report"], ["okr_planning", "OKR / Objectives Plan"],
      ["qbr_deck", "Quarterly Business Review"], ["stakeholder_update", "Stakeholder Update"],
      ["talking_points", "Talking Points"], ["meeting_brief", "Meeting Brief"],
      ["investor_update", "Investor / Partner Update"], ["press_statement", "Press / Public Statement"],
    ],
  },
  training: {
    field: "report_type",
    modes: [
      ["awareness_curriculum", "Awareness Curriculum"], ["role_based_training", "Role-Based Training"],
      ["onboarding_security", "New-Hire Onboarding"], ["awareness_campaign", "Awareness Campaign"],
      ["course_content", "Course Content"], ["microlearning", "Microlearning Series"],
      ["quiz_assessment", "Knowledge Assessment"], ["policy_training", "Policy-Based Training"],
      ["phishing_simulation", "Phishing Simulation"], ["compliance_training", "Compliance Training"],
      ["workshop_facilitation", "Workshop Facilitation"], ["tabletop_awareness", "Awareness Tabletop"],
    ],
  },
  governance: {
    field: "report_type",
    modes: [
      ["risk_assessment", "AI Risk Assessment"], ["impact_assessment", "Algorithmic Impact Assessment"],
      ["ai_inventory", "AI Inventory & Risk Tiering"], ["vendor_ai_assessment", "Third-Party AI Vendor Review"],
      ["policy_framework", "AI Governance Policy"], ["ai_use_policy", "Acceptable AI-Use Policy"],
      ["model_card", "AI Model Card"], ["transparency_disclosure", "Transparency Disclosures"],
      ["compliance_mapping", "Framework Compliance Mapping"], ["bias_fairness_review", "Bias & Fairness Review"],
      ["data_governance", "AI Data Governance"], ["incident_response_ai", "AI Incident Response Plan"],
    ],
  },
  legal: {
    field: "task_type",
    modes: [
      ["contract_review", "Contract Review"], ["risk_review", "Risk Review"],
      ["clause_extraction", "Clause Extraction"], ["contract_summary", "Plain-Language Summary"],
      ["data_protection_review", "Data Protection Review"], ["obligation_tracker", "Obligation Tracker"],
      ["contract_comparison", "Contract Comparison"], ["negotiation_playbook", "Negotiation Playbook"],
      ["rfp_legal_review", "RFP Legal Review"], ["legal_drafting", "Legal Drafting"],
      ["legal_letter", "Legal Letter"], ["legal_checklist", "Legal Checklist"],
    ],
  },
};

// Flat list of every mode as a palette-ready item.
export const ASSISTANT_MODE_ITEMS = Object.entries(MODES_BY_ASSISTANT).flatMap(([key, { modes }]) => {
  const a = ASSISTANT_BY_KEY[key];
  if (!a) return [];
  const short = a.label.replace(" Assistant", "");
  return modes.map(([value, label]) => ({
    id: `${key}:${value}`,
    assistantKey: key,
    assistantLabel: short,
    label,
    perm: a.perm,
    icon: a.icon,
    to: `/studio?assistant=${key}&mode=${value}`,
    // search string for cmdk filtering
    keywords: `${short} ${label} ${value.replace(/_/g, " ")}`,
  }));
});

// Valid mode values per assistant — used by pages to validate the ?mode= param.
export const MODE_FIELD = Object.fromEntries(
  Object.entries(MODES_BY_ASSISTANT).map(([k, v]) => [k, v.field]));
export const VALID_MODES = Object.fromEntries(
  Object.entries(MODES_BY_ASSISTANT).map(([k, v]) => [k, v.modes.map((m) => m[0])]));
